import React, { JSX, useMemo } from "react";
import { X, ExternalLink } from "lucide-react";
import { classes } from "@/utils";
import { StatusIcon } from "./StatusIcon";
import { transactionStatus } from "@/services/bridgeService";

const steps = [
  {
    step: "preparing",
    title: "Preparing Transaction",
    description: "Setting up your request",
  },
  {
    step: "awaiting",
    title: "Awaiting Signature",
    description: "Please sign the transaction in your wallet",
  },
  {
    step: "broadcasting",
    title: "Broadcasting",
    description: "Sending transaction to the network",
  },
  {
    step: "inBlock",
    title: "Confirmation",
    description: "Waiting for block finalization",
  },
];

type StatusModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  currentStep: transactionStatus;
  title: string;
  details?: JSX.Element;
};

export const StatusModal: React.FC<StatusModalProps> = ({
  isOpen,
  onClose,
  currentStep,
  title,
  details,
}) => {
  if (!isOpen) return null;
  const explorerLink = `https://explorer.analog.one/#/extrinsic/${currentStep?.extrinsicHash}`;

  const completedStepIndex = useMemo(
    () =>
      currentStep?.status === "finalized"
        ? 4
        : steps.findIndex((step) => step.step === currentStep?.status),
    [currentStep?.status]
  );

  const canClose =
    currentStep?.status === "finalized" || currentStep?.status === "error";

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-[2px] flex items-center justify-center z-50 px-4 xs:px-0">
      <div className="backdrop-blur-xl w-full max-w-[500px] xl:max-w-[570px] text-white/[48%] border border-white/10 rounded-[20px] popup-bg relative overflow-hidden">
        <div className="card border-0 z-0">
          <div className="relative popup-bg rounded-[20px] p-6 xl:p-8 z-[1]">
            <div className="flex justify-between items-start mb-4 md:mb-5 xl:mb-6 relative z-20">
              <h2 className="text-2xl xs:text-[26px] md:text-[28px]  xl:text-[32px] font-normal text-white leading-8 xs:leading-[38px] tracking-[0.96px]">
                {title}
              </h2>
              {canClose && (
                <button
                  onClick={onClose}
                  className="text-white transition-colors"
                >
                  <X size={28} />
                </button>
              )}
            </div>
            {details && details}
            <div className="max-h-[494px] overflow-y-auto">
              <div className="space-y-3 xs:space-y-4">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 rounded-2xl border border-darkBlack-800 bg-black/[23%] backdrop-blur-[7px] px-4 xs:px-5 py-[13px] xs:py-4"
                  >
                    <div className="flex-1">
                      <p className="font-normal leading-6 xs:leading-7 tracking-[0.48px] xs:tracking-[0.6px] text-base xs:text-lg md:text-xl text-white">
                        {step.title}
                      </p>
                      {step.description && (
                        <p className=" text-xs xs:text-sm font-normal leading-5 xs:leading-[22px] tracking-[0.36px] xs:tracking-[0.42px] text-white/70 mt-1 xs:mt-2">
                          {step.description}
                        </p>
                      )}
                    </div>
                    <div className="xs:mt-1">
                      <StatusIcon
                        status={
                          currentStep?.status === "error"
                            ? "error"
                            : index === completedStepIndex
                            ? "current"
                            : index < completedStepIndex
                            ? "completed"
                            : "pending"
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>

              {currentStep?.status === "error" && (
                <div className="mt-6 p-4 bg-red-500/[2%] border border-red-500/20 rounded-lg">
                  <p className="text-red-500 text-sm">
                    {currentStep.errorMessage}
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mt-6 xs:mt-7 md:mt-8">
              {explorerLink && currentStep?.status === "finalized" && (
                <div className="">
                  <a
                    href={explorerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes(
                      "flex items-center gap-2 text-white text-base md:text-lg tracking-[0.48px] md:tracking-[0.54px] leading-6 font-normal"
                      // currentStep?.status === 'error' && 'text-red-500'
                    )}
                  >
                    <ExternalLink size={24} />
                    <span>View transaction in explorer</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
